import { useEffect, useMemo, useRef, useState } from "react";
import Box from "elements/Box";
import AlertDialog from "elements/AlertDialog";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  AlertTitle,
  LinearProgress,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { ApiRequest } from "GlobalFunctions";
import { ProjectConfig } from "Global";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VerifiedIcon from "@mui/icons-material/Verified";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const defaultConfig = {
  environment: "habilitacion",
  software_id: "",
  software_pin: "",
  test_set_id: "",
  nit: "",
  business_name: "",
  tax_regime: "",
  is_enabled: false,
  has_certificate: false,
};

type SetupStep =
  | "needs_emisor"
  | "needs_software"
  | "needs_certificate"
  | "needs_resolutions"
  | "needs_testset"
  | "ready_for_production"
  | "active_production";

const STEP_LABELS: { key: SetupStep; label: string }[] = [
  { key: "needs_emisor", label: "Datos del emisor" },
  { key: "needs_certificate", label: "Certificado" },
  { key: "needs_software", label: "Credenciales" },
  { key: "needs_resolutions", label: "Resoluciones" },
  { key: "needs_testset", label: "Set de pruebas" },
  { key: "ready_for_production", label: "Listo" },
];

export default function DianConfiguration(props: any) {
  const { setLoading } = props;
  const { enqueueSnackbar } = useSnackbar();

  const [config, setConfig] = useState<any>(defaultConfig);
  const [resolutions, setResolutions] = useState<any[]>([]);
  const [certPassword, setCertPassword] = useState("");
  const [certFile, setCertFile] = useState<File | null>(null);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [activeStep, setActiveStep] = useState(0);

  const [testResolution, setTestResolution] = useState<any>({
    resolution_number: "18760000001",
    prefix: "SETP",
    range_from: 990000000,
    range_to: 995000000,
    valid_date_from: "2019-01-19",
    valid_date_to: "2030-01-19",
    technical_key: "",
  });

  const [testSetRunning, setTestSetRunning] = useState(false);
  const [testSetProgress, setTestSetProgress] = useState<{ step: number; total: number; detail: string } | null>(null);
  const [testSetResult, setTestSetResult] = useState<any>(null);
  const [testSetErrors, setTestSetErrors] = useState<string[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setLoading(false);
    loadConfig();
    loadResolutions();
    loadTestResolution();
    loadLastTestSet();
    return () => { if (abortRef.current) abortRef.current.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = () => {
    ApiRequest({
      url: "/dian/config/get",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      setResponse: (response: any) => {
        if (response.config) setConfig({ ...defaultConfig, ...response.config });
      },
    });
  };

  const loadResolutions = () => {
    ApiRequest({
      url: "/dian/resolutions/list",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      setResponse: (response: any) => {
        if (response.list) setResolutions(response.list);
      },
    });
  };

  const loadTestResolution = () => {
    ApiRequest({
      url: "/dian/resolutions/test",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      setResponse: (response: any) => {
        if (response.resolution) setTestResolution(response.resolution);
      },
    });
  };

  const loadLastTestSet = () => {
    ApiRequest({
      url: "/dian/testset/last",
      method: "get",
      headers: { ltkn: localStorage.getItem("ltkn") },
      setResponse: (response: any) => {
        if (response.last) setTestSetResult(response.last);
      },
    });
  };

  // ===== Step validation =====
  const isStepComplete = (stepIdx: number): boolean => {
    switch (stepIdx) {
      case 0: return !!(config.nit && config.business_name && config.tax_regime);
      case 1: return !!(config.has_certificate || (certFile && certPassword));
      case 2: return !!(config.software_id && config.software_pin && (config.environment !== "habilitacion" || config.test_set_id));
      case 3: return !!(testResolution.technical_key && testResolution.resolution_number);
      case 4: {
        const ts = testSetResult?.summary;
        return !!(testSetResult?.testSetAccepted || (ts && ts.total_accepted === ts.total_sent && ts.total_sent > 0));
      }
      case 5: return true;
      default: return false;
    }
  };

  // Max step user can navigate to (first incomplete step)
  const maxReachableStep = useMemo(() => {
    for (let i = 0; i < STEP_LABELS.length; i++) {
      if (!isStepComplete(i)) return i;
    }
    return STEP_LABELS.length - 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, validationResult, testResolution, testSetResult]);

  // Position on first incomplete step only on initial load
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!initializedRef.current && (config.nit || config.has_certificate || config.software_id)) {
      initializedRef.current = true;
      setActiveStep(maxReachableStep);
    }
  }, [maxReachableStep, config]);

  const canEnable = maxReachableStep >= 5 || config.is_enabled;

  // Auto-enable and switch to production when all steps complete
  useEffect(() => {
    if (canEnable && !config.is_enabled) {
      const newConfig = { ...config, is_enabled: true, environment: "produccion" };
      setConfig(newConfig);
      ApiRequest({
        url: "/dian/config/save",
        method: "post",
        headers: { ltkn: localStorage.getItem("ltkn") },
        body: newConfig,
        setResponse: () => {},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEnable]);

  const handleStepClick = (idx: number) => {
    if (idx <= maxReachableStep) setActiveStep(idx);
  };

  const handleNext = () => {
    if (!isStepComplete(activeStep)) {
      enqueueSnackbar("Completa este paso antes de continuar", { variant: "warning" });
      return;
    }
    if (activeStep === 0) {
      // Save emisor
      ApiRequest({
        url: "/dian/config/save",
        method: "post",
        headers: { ltkn: localStorage.getItem("ltkn") },
        body: config,
        setResponse: (response: any) => {
          if (response.status === "ok") setActiveStep(activeStep + 1);
          else enqueueSnackbar(response.error || "Error al guardar", { variant: "error" });
        },
      });
    } else if (activeStep === 1) {
      // Upload certificate if new file selected
      if (certFile) {
        if (!certPassword) { enqueueSnackbar("Ingresa la contraseña del certificado", { variant: "warning" }); return; }
        const formData = new FormData();
        formData.append("certificate", certFile);
        formData.append("password", certPassword);
        ApiRequest({
          url: "/dian/config/uploadCertificate",
          method: "post",
          headers: { ltkn: localStorage.getItem("ltkn") },
          body: formData,
          formData: true,
          setResponse: (response: any) => {
            if (response.status === "ok") {
              setCertFile(null);
              loadConfig();
              setActiveStep(activeStep + 1);
            } else {
              enqueueSnackbar(response.error || "Error al cargar certificado", { variant: "error" });
            }
          },
        });
      } else {
        // Already has certificate, just advance
        setActiveStep(activeStep + 1);
      }
    } else if (activeStep === 2) {
      // Save credentials + validate with DIAN
      ApiRequest({
        url: "/dian/config/save",
        method: "post",
        headers: { ltkn: localStorage.getItem("ltkn") },
        body: config,
        setResponse: (response: any) => {
          if (response.status !== "ok") {
            enqueueSnackbar(response.error || "Error al guardar", { variant: "error" });
            return;
          }
          setValidating(true);
          ApiRequest({
            url: "/dian/config/testConnection",
            method: "post",
            headers: { ltkn: localStorage.getItem("ltkn") },
            body: { environment: config.environment, software_id: config.software_id, software_pin: config.software_pin, test_set_id: config.test_set_id },
            setResponse: (r: any) => {
              setValidating(false);
              const success = r.status === "ok" && r.result?.success;
              setValidationResult({ success, message: r.result?.message || r.error || "Sin respuesta" });
              if (success) {
                enqueueSnackbar("Credenciales validadas con DIAN", { variant: "success" });
                setActiveStep(activeStep + 1);
              } else {
                enqueueSnackbar(r.result?.message || r.error || "Validación fallida", { variant: "error" });
              }
            },
          });
        },
      });
    } else if (activeStep === 3) {
      // Save test resolution
      ApiRequest({
        url: "/dian/resolutions/test",
        method: "post",
        headers: { ltkn: localStorage.getItem("ltkn") },
        body: testResolution,
        setResponse: (r: any) => {
          if (r.status === "ok") {
            loadTestResolution();
            setActiveStep(activeStep + 1);
          } else {
            enqueueSnackbar(r.error || "Error al guardar resolución", { variant: "error" });
          }
        },
      });
    } else {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  // ===== Handlers =====
  const handleConfigChange = (e: any) => {
    const { name, value } = e.target;
    setConfig({ ...config, [name]: value });
    setValidationResult(null);
  };

  const handleEnableSwitch = (e: any) => {
    if (!canEnable && e.target.checked) {
      enqueueSnackbar("Completa la configuración antes de habilitar", { variant: "warning" });
      return;
    }
    const newConfig = { ...config, is_enabled: e.target.checked };
    setConfig(newConfig);
    ApiRequest({
      url: "/dian/config/save",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: newConfig,
      setResponse: (r: any) => {
        if (r.status === "ok") enqueueSnackbar(e.target.checked ? "Módulo habilitado" : "Módulo deshabilitado", { variant: "success" });
      },
    });
  };

  const saveConfig = () => {
    ApiRequest({
      url: "/dian/config/save",
      method: "post",
      headers: { ltkn: localStorage.getItem("ltkn") },
      body: config,
      setResponse: (response: any) => {
        if (response.status === "ok") enqueueSnackbar("Configuración guardada", { variant: "success" });
        else enqueueSnackbar(response.error || "Error al guardar", { variant: "error" });
      },
    });
  };


  // ===== SSE para set de pruebas =====
  const [showTestSetConfirm, setShowTestSetConfirm] = useState(false);

  const runTestSet = async () => {
    setShowTestSetConfirm(false);
    setTestSetRunning(true);
    setTestSetProgress({ step: 0, total: 50, detail: "Iniciando…" });
    setTestSetResult(null);
    setTestSetErrors([]);
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const url = `${ProjectConfig.api_url}/dian/testset/run`;
      const ltkn = localStorage.getItem("ltkn") || "";
      const response = await fetch(url, {
        method: "POST",
        headers: { ltkn, Accept: "text/event-stream" },
        signal: ctrl.signal,
      });
      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const rawEvent = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = rawEvent.split("\n");
          let event = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
          if (!data) continue;
          let payload: any;
          try { payload = JSON.parse(data); } catch { payload = data; }
          if (event === "progress") {
            setTestSetProgress({ step: payload.step, total: payload.total, detail: payload.detail });
          } else if (event === "done") {
            setTestSetResult(payload);
            const errs: string[] = [];
            if (payload.results?.invoices?.errors) errs.push(...payload.results.invoices.errors);
            if (payload.results?.credit_notes?.errors) errs.push(...payload.results.credit_notes.errors);
            if (payload.results?.debit_notes?.errors) errs.push(...payload.results.debit_notes.errors);
            setTestSetErrors(errs);
            setTestSetRunning(false);
            enqueueSnackbar(payload.success ? "Set de pruebas completado" : "Set finalizado con errores", { variant: payload.success ? "success" : "warning" });
          } else if (event === "error") {
            setTestSetRunning(false);
            enqueueSnackbar(payload.error || "Error en set de pruebas", { variant: "error" });
          }
        }
      }
    } catch (error: any) {
      if (error.name !== "AbortError") enqueueSnackbar(`Error: ${error.message}`, { variant: "error" });
    } finally {
      setTestSetRunning(false);
      abortRef.current = null;
    }
  };


  // ===== Step content renderers =====
  const renderStepContent = () => {
    switch (activeStep) {
      case 0: return renderEmisor();
      case 1: return renderCertificado();
      case 2: return renderCredenciales();
      case 3: return renderResoluciones();
      case 4: return renderTestSet();
      case 5: return renderListo();
      default: return null;
    }
  };

  const renderEmisor = () => (
    <Box id="dianCompany" className="box100" title="Datos del Emisor">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 16, marginTop: 10 }}>
        <TextField label="NIT" name="nit" value={config.nit || ""} onChange={handleConfigChange} size="small" fullWidth />
        <TextField label="Razón Social" name="business_name" value={config.business_name || ""} onChange={handleConfigChange} size="small" fullWidth />
        <FormControl size="small" fullWidth>
          <InputLabel>Régimen Tributario</InputLabel>
          <Select name="tax_regime" value={config.tax_regime || ""} onChange={handleConfigChange} label="Régimen Tributario">
            <MenuItem value="O-13">O-13 Gran contribuyente</MenuItem>
            <MenuItem value="O-15">O-15 Autorretenedor</MenuItem>
            <MenuItem value="O-23">O-23 Agente de retención IVA</MenuItem>
            <MenuItem value="O-47">O-47 Régimen Simple</MenuItem>
            <MenuItem value="O-99">O-99 No responsable de IVA</MenuItem>
            <MenuItem value="R-99-PN">R-99-PN No responsable - Persona Natural</MenuItem>
          </Select>
        </FormControl>
      </div>
    </Box>
  );

  const renderCertificado = () => (
    <Box id="dianCertificate" className="box100" title="Certificado Digital">
      {config.has_certificate && !certFile ? (
        <div className="file_dropzone file_dropzone_active" style={{ pointerEvents: "none", position: "relative" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <VerifiedIcon color="success" fontSize="small" />
            Certificado cargado
          </span>
          <Button
            size="small" color="error" style={{ position: "absolute", top: 8, right: 8, pointerEvents: "all" }}
            onClick={(e) => { e.stopPropagation(); setCertFile(null); setConfig({ ...config, has_certificate: false }); }}
          >
            <CancelIcon fontSize="small" />
          </Button>
        </div>
      ) : (
        <>
          <div
            className={`file_dropzone ${certFile ? "file_dropzone_active" : ""}`}
            onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setCertFile(e.dataTransfer.files[0]); e.currentTarget.classList.remove("dropzone_active"); }}
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("dropzone_active"); }}
            onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove("dropzone_active"); }}
          >
            {certFile && (
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <VerifiedIcon color="success" fontSize="small" />
                {certFile.name}
              </span>
            )}
            <label id="dropFileDiv" className={certFile ? "showOnlyOnHover" : ""}>
              <CloudUploadIcon />
              <span>Arrastra o da click para subir un .p12 / .pfx</span>
              <input type="file" hidden accept=".p12,.pfx" onChange={(e) => { if (e.target.files?.[0]) setCertFile(e.target.files[0]); }} />
            </label>
          </div>
          {certFile && (
            <TextField
              label="Contraseña del certificado" value={certPassword} onChange={(e) => setCertPassword(e.target.value)}
              size="small" type="password" fullWidth sx={{ mt: 2, maxWidth: 400 }}
            />
          )}
        </>
      )}
    </Box>
  );

  const renderCredenciales = () => (
    <Box id="dianCredentials" className="box100" title="Credenciales de Software">
      <div style={{ display: "grid", gridTemplateColumns: config.environment === "habilitacion" ? "1fr 1fr 1fr" : "1fr 1fr", gap: 16, marginTop: 10 }}>
        <TextField label="Software ID" name="software_id" value={config.software_id || ""} onChange={handleConfigChange} size="small" fullWidth />
        <TextField
          label="Software PIN" name="software_pin" value={config.software_pin || ""} onChange={handleConfigChange} size="small" fullWidth
        />
        {config.environment === "habilitacion" && (
          <Tooltip title="ID asignado por DIAN MUISCA al solicitar la habilitación.">
            <TextField label="Test Set ID" name="test_set_id" value={config.test_set_id || ""} onChange={handleConfigChange} size="small" fullWidth helperText="Solo necesario en habilitación" />
          </Tooltip>
        )}
      </div>
      {validating && <LinearProgress sx={{ mt: 2 }} />}
      {validationResult && !validationResult.success && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Validación fallida</AlertTitle>
          {validationResult.message}
        </Alert>
      )}
    </Box>
  );

  const renderResoluciones = () => (
    <Box id="dianTestResolution" className="box100" title="Resolución de Pruebas (Habilitación)">
      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Resolución de pruebas DIAN</AlertTitle>
        Confirma los datos de resolución y rangos de pruebas.
      </Alert>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <TextField label="No. Resolución" value={testResolution.resolution_number || ""} onChange={(e) => setTestResolution({ ...testResolution, resolution_number: e.target.value })} size="small" fullWidth />
        <TextField label="Prefijo" value={testResolution.prefix || ""} onChange={(e) => setTestResolution({ ...testResolution, prefix: e.target.value })} size="small" fullWidth />
        <TextField label="Rango Desde" type="number" value={testResolution.range_from || ""} onChange={(e) => setTestResolution({ ...testResolution, range_from: parseInt(e.target.value) || 0 })} size="small" fullWidth />
        <TextField label="Rango Hasta" type="number" value={testResolution.range_to || ""} onChange={(e) => setTestResolution({ ...testResolution, range_to: parseInt(e.target.value) || 0 })} size="small" fullWidth />
        <TextField label="Vigencia desde" type="date" value={testResolution.valid_date_from ? String(testResolution.valid_date_from).slice(0, 10) : ""} onChange={(e) => setTestResolution({ ...testResolution, valid_date_from: e.target.value })} size="small" fullWidth InputLabelProps={{ shrink: true }} />
        <TextField label="Vigencia hasta" type="date" value={testResolution.valid_date_to ? String(testResolution.valid_date_to).slice(0, 10) : ""} onChange={(e) => setTestResolution({ ...testResolution, valid_date_to: e.target.value })} size="small" fullWidth InputLabelProps={{ shrink: true }} />
        <TextField
          label="Clave Técnica (TechnicalKey)" value={testResolution.technical_key || ""}
          onChange={(e) => setTestResolution({ ...testResolution, technical_key: e.target.value })}
          size="small" fullWidth required error={!testResolution.technical_key}
          helperText="Cadena hexadecimal del portal MUISCA — obligatoria para firmar"
          sx={{ gridColumn: "1 / -1" }}
        />
      </div>
    </Box>
  );

  const renderTestSet = () => (
    <Box id="dianTestSet" className="box100" title="Set de Pruebas DIAN">
      <div style={{ marginTop: 10 }}>
        <p style={{ color: "#666", marginBottom: 12, fontSize: 13 }}>
          Envía los 50 documentos requeridos por DIAN (30 facturas, 10 notas crédito, 10 notas débito).
        </p>
        {!testSetRunning && (
          <Button
            variant="contained" color="success" startIcon={<PlayArrowIcon />} onClick={() => setShowTestSetConfirm(true)} size="large"
            disabled={!config.software_id || !config.test_set_id || !config.has_certificate || !testResolution.resolution_id}
          >
            Ejecutar Set de Pruebas
          </Button>
        )}
        {testSetRunning && testSetProgress && (
          <div style={{ maxWidth: 400, margin: "16px auto 0", textAlign: "center" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontWeight: 500, fontSize: 13 }}>{testSetProgress.detail}</span>
              <span style={{ fontSize: 13 }}>{testSetProgress.step} / {testSetProgress.total}</span>
            </div>
            <LinearProgress variant="determinate" value={(testSetProgress.step / testSetProgress.total) * 100} style={{ height: 6, borderRadius: 3 }} />
          </div>
        )}
        {testSetResult && testSetResult.summary && (
          <Alert severity={testSetResult.success ? "success" : "warning"} sx={{ mt: 2 }}>
            <AlertTitle>{testSetResult.testSetAccepted ? "✅ Set de Pruebas APROBADO" : "Resultado del Set de Pruebas"}</AlertTitle>
            {testSetResult.testSetAccepted ? (
              <>{testSetResult.summary.message || "La DIAN ha confirmado que el set de pruebas fue aceptado."}</>
            ) : (
              <>
                Facturas: <strong>{testSetResult.summary.invoices}</strong><br />
                Notas Crédito: <strong>{testSetResult.summary.credit_notes}</strong><br />
                Notas Débito: <strong>{testSetResult.summary.debit_notes}</strong><br />
                <strong>Total aceptados: {testSetResult.summary.total_accepted}/{testSetResult.summary.total_sent}</strong>
                {testSetResult.summary.total_errors > 0 && <span style={{ color: "#d32f2f" }}> — {testSetResult.summary.total_errors} errores</span>}
              </>
            )}
          </Alert>
        )}
        {testSetErrors.length > 0 && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>Ver errores ({testSetErrors.length})</AccordionSummary>
            <AccordionDetails>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                {testSetErrors.map((e, i) => <li key={i} style={{ marginBottom: 4 }}>{e}</li>)}
              </ul>
            </AccordionDetails>
          </Accordion>
        )}
      </div>
    </Box>
  );

  const renderListo = () => (
    <Box id="dianReady" className="box100" title="Configuración Completa">
      <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 1 }}>
        <AlertTitle>¡Todo listo!</AlertTitle>
        {config.environment === "habilitacion"
          ? "Set de pruebas aprobado. Puedes pasar a producción."
          : "Todo configurado. Activa el módulo cuando estés listo para emitir."}
      </Alert>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 16 }}>
        <div><span style={{ fontSize: 12, color: "#888" }}>NIT</span><div style={{ fontWeight: 500 }}>{config.nit}</div></div>
        <div><span style={{ fontSize: 12, color: "#888" }}>Razón Social</span><div style={{ fontWeight: 500 }}>{config.business_name}</div></div>
        <div><span style={{ fontSize: 12, color: "#888" }}>Certificado</span><div>{config.has_certificate ? <Chip icon={<VerifiedIcon />} label="Vigente" color="success" size="small" /> : <Chip label="Sin certificado" color="error" size="small" />}</div></div>
      </div>
      <div style={{ borderTop: "1px solid #eee", paddingTop: 12, marginTop: 16 }}>
        <Button variant="outlined" color="error" size="small" onClick={() => setShowUnlinkDialog(true)}>
          Desvincular software e iniciar de nuevo
        </Button>
      </div>
    </Box>
  );

  // ===== Main render =====
  return (
    <div id="DianConfiguration" className="ModuleContainer">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Facturación Electrónica DIAN</h2>
            <span className="h2_subtitle">Configuración del módulo de facturación electrónica</span>
          </div>
        </div>

        {canEnable ? (
          /* ===== COMPLETED VIEW: no wizard, just summary ===== */
          <Box id="dianCompleted" className="box100">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <Chip icon={<CheckCircleIcon />} label="Facturación electrónica activa" color="success" />
            </div>

            <div style={{ display: "flex", gap: 32, flexWrap: "wrap", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: 0.5 }}>NIT</div>
                <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{config.nit}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, textTransform: "uppercase", color: "#888", letterSpacing: 0.5 }}>Razón Social</div>
                <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{config.business_name}</div>
              </div>
            </div>

            <div style={{ borderTop: "1px solid #eee", paddingTop: 16, marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <FormControlLabel
                control={<Switch checked={config.is_enabled} onChange={handleEnableSwitch} />}
                label={config.is_enabled ? "Módulo habilitado" : "Módulo deshabilitado"}
              />
              <Button variant="outlined" color="error" size="small" onClick={() => setShowUnlinkDialog(true)}>
                Desvincular software
              </Button>
            </div>
          </Box>
        ) : (
          /* ===== WIZARD VIEW ===== */
          <>
            <Box id="dianStatus" className="box100">
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Chip icon={<CancelIcon />} label="MÓDULO INACTIVO" color="default" />
                </div>
                <Tooltip title="Completa todos los pasos antes de habilitar">
                  <FormControlLabel
                    control={<Switch checked={false} disabled />}
                    label="Deshabilitado"
                  />
                </Tooltip>
              </div>
              <div style={{ marginTop: 16 }}>
                <Stepper activeStep={activeStep} alternativeLabel nonLinear>
                  {STEP_LABELS.map((s, idx) => (
                    <Step key={s.key} completed={isStepComplete(idx) && idx < activeStep}>
                      <StepButton
                        onClick={() => handleStepClick(idx)}
                        disabled={idx > maxReachableStep}
                        sx={{ cursor: idx <= maxReachableStep ? "pointer" : "not-allowed" }}
                      >
                        <StepLabel>{s.label}</StepLabel>
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
              </div>
            </Box>

            {renderStepContent()}

            <Box id="dianNav" className="box100">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack} disabled={activeStep === 0}>
                  Anterior
                </Button>
                <span style={{ fontSize: 13, color: "#888" }}>Paso {activeStep + 1} de {STEP_LABELS.length}</span>
                <Button variant="contained" endIcon={<ArrowForwardIcon />} onClick={handleNext} disabled={activeStep >= STEP_LABELS.length - 1 || !isStepComplete(activeStep)}>
                  Continuar
                </Button>
              </div>
            </Box>
          </>
        )}

        <AlertDialog
          open={showUnlinkDialog}
          setOpen={setShowUnlinkDialog}
          subtitle="Se borrará toda la configuración DIAN. Esta acción no se puede deshacer."
          send={() => {
            const resetData = { ...defaultConfig, environment: "habilitacion" };
            ApiRequest({
              url: "/dian/config/save",
              method: "post",
              headers: { ltkn: localStorage.getItem("ltkn") },
              body: { ...resetData, reset: true },
              setResponse: (response: any) => {
                if (response.status === "ok") {
                  setConfig(resetData);
                  setValidationResult(null);
                  setTestSetResult(null);
                  setResolutions([]);
                  setActiveStep(0);
                  enqueueSnackbar("Software desvinculado.", { variant: "info" });
                } else enqueueSnackbar("Error al desvincular", { variant: "error" });
              },
            });
          }}
        />

        <AlertDialog
          open={showTestSetConfirm}
          setOpen={setShowTestSetConfirm}
          subtitle="Se enviarán 50 documentos a la DIAN. El proceso toma varios minutos."
          send={runTestSet}
        />
      </div>
    </div>
  );
}
